// @flow
import { IS_SERVER } from "../../constants/Constants";
import { getSetting } from "../../constants/Settings";

import ErrorResponse from "../../models/error/ErrorResponse";

import { push } from "../_router/RouterActions";
import { showXHRErrorNotification } from "./Notification";
import { resetProgress } from "./ProgressIndicator";
import { changePassword } from "./SignIn";
import { handleUnauthorized } from "./Authorization";
import { noAction } from "./Application";

import type { FetchException } from "../../exceptions";
import type { Dispatch, SaveErrorAction, ThunkAction } from "../types";

type DefaultErrorResponseHandler = (error: ErrorResponse) => ThunkAction<>;
type CustomErrorResponseHandler = (
  errorResponse: ErrorResponse,
  defaultHandler: DefaultErrorResponseHandler,
) => ThunkAction<>;

/**
 */
const saveError = (error: ErrorResponse): SaveErrorAction => ({
  type: "SAVE_ERROR",
  payload: error,
});

/**
 */
const defaultErrorResponseHandler: DefaultErrorResponseHandler =
  (error: ErrorResponse): ThunkAction<> =>
  (dispatch: Dispatch) => {
    if (error.isResourceNotFoundAfterReload || error.isRemoteServiceException) {
      return dispatch(noAction());
    }

    if (error.isUnauthorized) {
      return dispatch(handleUnauthorized(error));
    }

    if (error.isBlocked || error.isConcurrentUser || error.isInvalidUsername) {
      const LOGOUT_PATH = getSetting("LOGOUT_PATH", "/signout");
      dispatch(push(LOGOUT_PATH));
    }

    if (error.isChangePassword) {
      return dispatch(changePassword());
    }

    if (IS_SERVER) {
      return dispatch(saveError(error));
    }

    // trigger a global error, not being caught by a promise
    setTimeout(() => {
      throw error.error;
    });

    return dispatch(showXHRErrorNotification(error));
  };

/**
 * Standard Custom error response, acts as a proxy and can be overwritten by {@link setCustomErrorResponseHandler}
 */
let customErrorResponseHandler: CustomErrorResponseHandler =
  (errorResponse, defaultHandler) => (dispatch: Dispatch) =>
    dispatch(defaultHandler(errorResponse));

/**
 * Set a custom error response handler,
 * can be used when custom functionality is needed on certain (error) response
 * of the modular ui, for example when custom logic is needed when a user is unauthorized<br>
 * It receives an {@link ErrorResponse} and the {@link defaultErrorResponseHandler}
 */
export const setCustomErrorResponseHandler = (
  handler: CustomErrorResponseHandler,
) => {
  customErrorResponseHandler = handler;
};

/**
 * Handle errors by sending an error notification message
 */
export const handleError =
  (error: Error | FetchException): ThunkAction<> =>
  (dispatch: Dispatch) => {
    dispatch(resetProgress());

    const errorResponse = new ErrorResponse(error);

    return dispatch(
      customErrorResponseHandler(errorResponse, defaultErrorResponseHandler),
    );
  };
